import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Isolates a single visual/table render so an uncaught exception (e.g. a bad
 * width/layout computation while resizing a table to full page width) shows an
 * inline fallback instead of white-screening the entire report/app. Each
 * boundary resets when its `resetKey` changes (so fixing the cause re-renders).
 */
export class VisualErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || 'Render error' };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Visual render error:', error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, message: '' });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="w-full h-full min-h-[80px] flex flex-col items-center justify-center text-center p-3 rounded-lg border border-amber-500/30 bg-amber-500/5"
          data-testid="visual-error-fallback"
        >
          <AlertTriangle className="w-5 h-5 text-amber-500 mb-1.5" />
          <span className="text-[11px] font-semibold text-amber-600">This visual couldn't be displayed</span>
          <span className="text-[9px] text-muted-foreground mt-0.5 max-w-[90%] truncate">{this.props.label || this.state.message}</span>
        </div>
      );
    }
    return this.props.children;
  }
}
